// components/Training/TrainingRosterPage.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import {
  Text, Button, Input, Textarea, Field, Badge, Spinner,
  Dialog, DialogSurface, DialogTitle, DialogBody, DialogActions,
  MessageBar, MessageBarBody, Tooltip,
} from "@fluentui/react-components";
import {
  Add24Regular, Delete24Regular, Edit24Regular, Mail24Regular,
  CalendarLtr24Regular, ChevronLeft24Regular, ChevronRight24Regular,
  Person24Regular, BuildingMultiple24Regular, BookOpen24Regular,
  Location24Regular, Alert24Regular, Dismiss24Regular, Search24Regular,
  SendClock24Regular, CheckmarkCircle24Regular, Info24Regular, Clock24Regular,
} from "@fluentui/react-icons";
import { useUserAccess } from "../../context/UserAccessContext";
import { getAccessToken } from "../../services/msalService";
import { userSearchRequest } from "../../authConfig";
import {
  fetchTrainingRoster, createTrainingEntry, updateTrainingEntry, deleteTrainingEntry,
  loadLocalRoster, saveLocalRoster, makeLocalEntry, sendTrainingReminder,
} from "../../services/trainingService";

// ── Calendar helpers ───────────────────────────────────────────────────────────
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DAYS   = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

function toDateStr(year, month, day) {
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function formatDisplay(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr + "T12:00:00").toLocaleDateString("en-GB", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });
}

function formatShort(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr + "T12:00:00").toLocaleDateString("en-GB", {
    day: "numeric", month: "short", year: "numeric",
  });
}

function addDays(dateStr, n) {
  const d = new Date(dateStr + "T12:00:00");
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

const todayStr = new Date().toISOString().slice(0, 10);
const isFriday = new Date().getDay() === 5;

function formatTime(t) {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${ampm}`;
}

// ── Sub-components ─────────────────────────────────────────────────────────────
function Avatar({ name, size = 40, color = "#002D7A" }) {
  const initials = (name || "?").split(" ").map(p => p[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: color, color: "#fff",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontWeight: 700, fontSize: size * 0.35, flexShrink: 0,
      letterSpacing: 0.5,
    }}>
      {initials}
    </div>
  );
}

function InfoRow({ icon, label, value }) {
  if (!value) return null;
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 12 }}>
      <div style={{ color: "#7A86A0", flexShrink: 0, marginTop: 2 }}>{icon}</div>
      <div>
        <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 1 }}>{label}</div>
        <div style={{ fontSize: 14, color: "#201F1E", lineHeight: 1.5 }}>{value}</div>
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function TrainingRosterPage() {
  const { instance, accounts } = useMsal();
  const { isAdmin, isEditor, currentUserEmail, currentUserName, isDemoMode } = useUserAccess();
  const canManage = isAdmin || isEditor;

  const today = new Date();
  const [year,  setYear]  = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [selectedDate, setSelectedDate] = useState(todayStr);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [saving, setSaving]     = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [sendingIds, setSendingIds] = useState(new Set());
  const [toast, setToast] = useState(null);
  const [sendingAll, setSendingAll]  = useState(false);
  const [userSuggestions, setUserSuggestions] = useState([]);
  const [userSearching, setUserSearching] = useState(false);
  const userSearchTimer = useRef(null);

  const EMPTY_FORM = { date: selectedDate, handlerName: "", handlerEmail: "", handlerDept: "", topic: "", time: "", venue: "", notes: "" };
  const [form, setForm] = useState(EMPTY_FORM);

  // ── Load ─────────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getAccessToken(instance, accounts);
      const list = await fetchTrainingRoster(token);
      setEntries(list);
    } catch {
      setEntries(loadLocalRoster());
    } finally {
      setLoading(false);
    }
  }, [instance, accounts]);

  useEffect(() => { load(); }, [load]);

  // ── Toast helper ─────────────────────────────────────────────────────────────
  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  // ── Derived ──────────────────────────────────────────────────────────────────
  const entryMap = {};
  entries.forEach(e => { entryMap[e.date] = e; });

  const selectedEntry = selectedDate ? entryMap[selectedDate] : null;

  const upcomingEntries = entries
    .filter(e => e.date >= todayStr && e.date <= addDays(todayStr, 14))
    .sort((a, b) => a.date.localeCompare(b.date));

  const hasTodaySession  = upcomingEntries.some(e => e.date === todayStr);
  const upcomingLabel    = hasTodaySession ? "Today & Upcoming" : "Upcoming Sessions";
  const upcomingCount    = upcomingEntries.length;
  const upcomingSubtext  = hasTodaySession && upcomingCount === 1
    ? "1 session scheduled for today"
    : hasTodaySession
      ? `${upcomingCount} sessions — including today`
      : `${upcomingCount} session${upcomingCount !== 1 ? "s" : ""} coming up`;

  // ── Calendar grid ─────────────────────────────────────────────────────────────
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDow    = new Date(year, month, 1).getDay();
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  // Pad to complete week
  while (cells.length % 7 !== 0) cells.push(null);

  function prevMonth() {
    if (month === 0) { setMonth(11); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  }
  function nextMonth() {
    if (month === 11) { setMonth(0); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  }

  // ── O365 user search ──────────────────────────────────────────────────────────
  async function searchUsers(q) {
    if (!q || q.length < 2) { setUserSuggestions([]); return; }
    setUserSearching(true);
    try {
      const token = await instance.acquireTokenSilent({ ...userSearchRequest, account: accounts[0] }).then(r => r.accessToken);
      const res = await fetch(
        `https://graph.microsoft.com/v1.0/users?$search="displayName:${encodeURIComponent(q)}"&$select=displayName,mail,department&$top=8`,
        { headers: { Authorization: `Bearer ${token}`, ConsistencyLevel: "eventual" } },
      );
      const data = await res.json();
      setUserSuggestions(data.value || []);
    } catch {
      setUserSuggestions([]);
    } finally {
      setUserSearching(false);
    }
  }

  function onHandlerSearch(val) {
    setForm(f => ({ ...f, handlerName: val }));
    clearTimeout(userSearchTimer.current);
    userSearchTimer.current = setTimeout(() => searchUsers(val), 300);
  }

  function pickUser(u) {
    setForm(f => ({ ...f, handlerName: u.displayName, handlerEmail: u.mail || "", handlerDept: u.department || "" }));
    setUserSuggestions([]);
  }

  // ── CRUD ──────────────────────────────────────────────────────────────────────
  function openAdd(date) {
    setEditTarget(null);
    setForm({ ...EMPTY_FORM, date: date || selectedDate });
    setUserSuggestions([]);
    setShowModal(true);
  }

  function openEdit(entry) {
    setEditTarget(entry);
    setForm({
      date: entry.date, handlerName: entry.handlerName, handlerEmail: entry.handlerEmail,
      handlerDept: entry.handlerDept, topic: entry.topic, time: entry.time || "", venue: entry.venue, notes: entry.notes,
    });
    setUserSuggestions([]);
    setShowModal(true);
  }

  async function handleSave() {
    if (!canManage) { showToast("Only admins and editors can manage entries.", "error"); return; }
    if (!form.topic.trim())        { showToast("Training topic is required.", "error"); return; }
    if (!form.handlerName.trim())  { showToast("Handler name is required.", "error");  return; }
    if (!form.handlerEmail.trim()) { showToast("Handler email is required.", "error"); return; }
    setSaving(true);
    try {
      if (isDemoMode) {
        if (editTarget) {
          const updated = entries.map(e => e.id === editTarget.id ? { ...e, ...form } : e);
          setEntries(updated);
          saveLocalRoster(updated);
        } else {
          const newEntry = makeLocalEntry(form, currentUserEmail);
          const updated = [...entries, newEntry];
          setEntries(updated);
          saveLocalRoster(updated);
          setSelectedDate(form.date);
        }
      } else {
        const token = await getAccessToken(instance, accounts);
        if (editTarget) {
          await updateTrainingEntry(token, editTarget._spItemId, form);
          setEntries(prev => prev.map(e => e.id === editTarget.id ? { ...e, ...form } : e));
        } else {
          const created = await createTrainingEntry(token, form);
          setEntries(prev => [...prev, created]);
          setSelectedDate(form.date);
        }
      }
      setShowModal(false);
      showToast(editTarget ? "Training entry updated." : "Training entry added.");
    } catch (err) {
      showToast(err.message || "Save failed.", "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(entry) {
    if (!canManage) { showToast("Only admins and editors can delete entries.", "error"); return; }
    setDeleting(true);
    try {
      if (isDemoMode) {
        const updated = entries.filter(e => e.id !== entry.id);
        setEntries(updated);
        saveLocalRoster(updated);
      } else {
        const token = await getAccessToken(instance, accounts);
        await deleteTrainingEntry(token, entry._spItemId);
        setEntries(prev => prev.filter(e => e.id !== entry.id));
      }
      setConfirmDelete(false);
      showToast("Entry removed.");
    } catch (err) {
      showToast(err.message || "Delete failed.", "error");
    } finally {
      setDeleting(false);
    }
  }

  // ── Reminders ─────────────────────────────────────────────────────────────────
  async function sendReminder(entry) {
    setSendingIds(s => new Set(s).add(entry.id));
    try {
      await sendTrainingReminder(instance, accounts, entry, currentUserName);
      showToast(`Reminder sent to ${entry.handlerName}.`);
    } catch (err) {
      showToast(err.message || "Failed to send reminder.", "error");
    } finally {
      setSendingIds(s => { const n = new Set(s); n.delete(entry.id); return n; });
    }
  }

  async function sendAllUpcomingReminders() {
    if (!upcomingEntries.length) return;
    setSendingAll(true);
    let ok = 0, fail = 0;
    for (const e of upcomingEntries) {
      try { await sendTrainingReminder(instance, accounts, e, currentUserName); ok++; }
      catch { fail++; }
    }
    setSendingAll(false);
    showToast(fail ? `${ok} reminders sent, ${fail} failed.` : `${ok} reminder${ok !== 1 ? "s" : ""} sent successfully.`, fail ? "warning" : "success");
  }

  // ── UI styles ─────────────────────────────────────────────────────────────────
  const COLORS = {
    navy: "#001A3D", blue: "#0066FF", white: "#fff",
    bg: "#F3F4F8", card: "#fff", border: "#E5E8F0",
    muted: "#7A86A0", text: "#201F1E",
  };

  // ── Detail panel content ──────────────────────────────────────────────────────
  function DetailPanel() {
    const entry = selectedEntry;
    const dateLabel = selectedDate ? formatDisplay(selectedDate) : "—";
    const isPast = selectedDate < todayStr;

    return (
      <div style={{ display: "flex", flexDirection: "column", height: "100%", gap: 0 }}>
        {/* Date header */}
        <div style={{ padding: "20px 24px 16px", borderBottom: `1px solid ${COLORS.border}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <CalendarLtr24Regular style={{ color: COLORS.blue, fontSize: 18 }} />
            <Text size={500} weight="semibold" style={{ color: COLORS.navy }}>
              {selectedDate ? `${new Date(selectedDate + "T12:00:00").toLocaleDateString("en-GB", { weekday: "long" })}, ${formatShort(selectedDate)}` : "Select a date"}
            </Text>
          </div>
          {selectedDate === todayStr && (
            <Badge appearance="tint" color="informative" style={{ fontSize: 10 }}>Today</Badge>
          )}
          {isPast && selectedDate !== todayStr && (
            <Badge appearance="outline" style={{ fontSize: 10, color: COLORS.muted }}>Past</Badge>
          )}
        </div>

        {/* Entry or empty state */}
        {entry ? (
          <div style={{ flex: 1, padding: "20px 24px", overflowY: "auto" }}>
            {/* Handler card */}
            <div style={{ display: "flex", gap: 14, alignItems: "center", marginBottom: 22, padding: "16px", background: "#EBF3FF", borderRadius: 12 }}>
              <Avatar name={entry.handlerName} size={48} color={COLORS.navy} />
              <div>
                <Text weight="semibold" size={400} style={{ color: COLORS.navy, display: "block" }}>{entry.handlerName}</Text>
                {entry.handlerDept && <Text style={{ fontSize: 12, color: COLORS.muted, display: "block" }}>{entry.handlerDept}</Text>}
                {entry.handlerEmail && <Text style={{ fontSize: 12, color: COLORS.blue, display: "block" }}>{entry.handlerEmail}</Text>}
              </div>
            </div>

            <InfoRow icon={<BookOpen24Regular />}        label="Topic"   value={entry.topic} />
            <InfoRow icon={<Clock24Regular />}           label="Time"    value={formatTime(entry.time)} />
            <InfoRow icon={<Location24Regular />}        label="Venue"   value={entry.venue} />
            {entry.notes && (
              <div style={{ marginBottom: 12, padding: "12px 14px", background: "#FAFAFA", border: `1px solid ${COLORS.border}`, borderRadius: 8 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 4 }}>Notes</div>
                <div style={{ fontSize: 13, color: "#555", lineHeight: 1.6 }}>{entry.notes}</div>
              </div>
            )}

            {/* Actions */}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 20 }}>
              {canManage && (
                <>
                  <Button
                    appearance="primary"
                    icon={sendingIds.has(entry.id) ? <Spinner size="tiny" /> : <Mail24Regular />}
                    onClick={() => sendReminder(entry)}
                    disabled={sendingIds.has(entry.id) || !entry.handlerEmail}
                    style={{ background: COLORS.navy }}
                  >
                    Send Reminder
                  </Button>
                  <Button icon={<Edit24Regular />} onClick={() => openEdit(entry)}>Edit</Button>
                  <Button icon={<Delete24Regular />} onClick={() => setConfirmDelete(true)} style={{ color: "#C4262E" }}>Delete</Button>
                </>
              )}
            </div>
          </div>
        ) : (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px 24px", textAlign: "center" }}>
            <div style={{ width: 52, height: 52, borderRadius: 14, background: "#F0F4FF", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14 }}>
              <CalendarLtr24Regular style={{ fontSize: 24, color: COLORS.blue }} />
            </div>
            <Text size={400} weight="semibold" style={{ color: COLORS.navy, display: "block", marginBottom: 6 }}>
              No training scheduled
            </Text>
            <Text style={{ fontSize: 13, color: COLORS.muted, marginBottom: 20 }}>
              {isPast ? "No training was recorded for this date." : "This date has no training session yet."}
            </Text>
            {canManage && !isPast && (
              <Button appearance="primary" icon={<Add24Regular />} onClick={() => openAdd(selectedDate)} style={{ background: COLORS.blue }}>
                Schedule Training
              </Button>
            )}
          </div>
        )}

        {/* Delete confirm */}
        {confirmDelete && selectedEntry && (
          <Dialog open onOpenChange={() => setConfirmDelete(false)}>
            <DialogSurface>
              <DialogTitle>Remove Training Entry</DialogTitle>
              <DialogBody>
                <Text>Remove the training entry for <strong>{selectedEntry.handlerName}</strong> on <strong>{formatDisplay(selectedEntry.date)}</strong>? This cannot be undone.</Text>
              </DialogBody>
              <DialogActions>
                <Button appearance="outline" onClick={() => setConfirmDelete(false)}>Cancel</Button>
                <Button
                  appearance="primary"
                  onClick={() => handleDelete(selectedEntry)}
                  disabled={deleting}
                  icon={deleting ? <Spinner size="tiny" /> : null}
                  style={{ background: "#C4262E" }}
                >
                  {deleting ? "Removing…" : "Remove"}
                </Button>
              </DialogActions>
            </DialogSurface>
          </Dialog>
        )}
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 0, minHeight: 0 }}>

      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed", top: 80, right: 24, zIndex: 9999,
          background: toast.type === "error" ? "#FDE7E9" : toast.type === "warning" ? "#FFF8E1" : "#E9F5E9",
          border: `1px solid ${toast.type === "error" ? "#F4ABB0" : toast.type === "warning" ? "#FFD54F" : "#A3D9A5"}`,
          borderRadius: 10, padding: "12px 18px", maxWidth: 360,
          display: "flex", alignItems: "center", gap: 10, boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
        }}>
          {toast.type === "success"
            ? <CheckmarkCircle24Regular style={{ color: "#107C10", fontSize: 18, flexShrink: 0 }} />
            : <Info24Regular style={{ color: toast.type === "warning" ? "#B8860B" : "#C4262E", fontSize: 18, flexShrink: 0 }} />
          }
          <Text style={{ fontSize: 13, color: "#1A1A2E", flex: 1 }}>{toast.msg}</Text>
          <button onClick={() => setToast(null)} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, color: COLORS.muted }}>
            <Dismiss24Regular style={{ fontSize: 16 }} />
          </button>
        </div>
      )}

      {/* Page header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <Text size={600} weight="bold" style={{ color: COLORS.navy, display: "block" }}>Safety Moment & KSS Roster</Text>
          <Text style={{ fontSize: 13, color: COLORS.muted }}>
            Manage Safety Moment & KSS schedule · {entries.length} session{entries.length !== 1 ? "s" : ""} total
          </Text>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {canManage && upcomingEntries.length > 0 && (
            <Tooltip content={`Send reminders to ${upcomingCount} handler${upcomingCount !== 1 ? "s" : ""} — ${upcomingSubtext}`} relationship="label">
              <Button
                appearance="outline"
                icon={sendingAll ? <Spinner size="tiny" /> : <SendClock24Regular />}
                onClick={sendAllUpcomingReminders}
                disabled={sendingAll}
                style={{ borderColor: "#0066FF", color: "#0066FF" }}
              >
                {sendingAll ? "Sending…" : `Send Reminders (${upcomingEntries.length})`}
              </Button>
            </Tooltip>
          )}
          {canManage && (
            <Button appearance="primary" icon={<Add24Regular />} onClick={() => openAdd()} style={{ background: COLORS.navy }}>
              Add Training
            </Button>
          )}
        </div>
      </div>

      {/* Friday reminder banner */}
      {isFriday && canManage && upcomingEntries.length > 0 && (
        <div style={{ marginBottom: 16, padding: "12px 18px", background: "#FFF8E1", border: "1px solid #FFD54F", borderRadius: 10, display: "flex", alignItems: "center", gap: 12 }}>
          <Alert24Regular style={{ color: "#B8860B", flexShrink: 0 }} />
          <Text style={{ fontSize: 13, color: "#7A5800", flex: 1 }}>
            <strong>It's Friday!</strong> {upcomingSubtext.charAt(0).toUpperCase() + upcomingSubtext.slice(1)}. Don't forget to send reminders to the handlers.
          </Text>
          <Button size="small" appearance="outline" onClick={sendAllUpcomingReminders} disabled={sendingAll} style={{ borderColor: "#B8860B", color: "#7A5800" }}>
            {sendingAll ? <Spinner size="tiny" /> : "Send Now"}
          </Button>
        </div>
      )}

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 80 }}>
          <Spinner size="large" label="Loading Safety Moment & KSS roster…" />
        </div>
      ) : (
        <div style={{ display: "flex", gap: 0, background: COLORS.card, borderRadius: 16, border: `1px solid ${COLORS.border}`, overflow: "hidden", boxShadow: "0 2px 12px rgba(0,0,0,0.06)", minHeight: 600, flexWrap: "wrap" }}>

          {/* ── Left: Calendar ── */}
          <div style={{ flex: "0 0 auto", width: "min(100%, 440px)", borderRight: `1px solid ${COLORS.border}`, display: "flex", flexDirection: "column" }}>

            {/* Month nav */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 20px", borderBottom: `1px solid ${COLORS.border}` }}>
              <Button appearance="subtle" icon={<ChevronLeft24Regular />} onClick={prevMonth} />
              <Text weight="semibold" size={400} style={{ color: COLORS.navy }}>
                {MONTHS[month]} {year}
              </Text>
              <Button appearance="subtle" icon={<ChevronRight24Regular />} onClick={nextMonth} />
            </div>

            {/* Day names */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", padding: "8px 12px 0" }}>
              {DAYS.map(d => (
                <div key={d} style={{ textAlign: "center", fontSize: 11, fontWeight: 700, color: COLORS.muted, padding: "4px 0", letterSpacing: 0.3 }}>
                  {d}
                </div>
              ))}
            </div>

            {/* Day cells */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 2, padding: "4px 12px 16px" }}>
              {cells.map((day, i) => {
                if (!day) return <div key={`empty-${i}`} />;
                const ds  = toDateStr(year, month, day);
                const has = !!entryMap[ds];
                const isToday    = ds === todayStr;
                const isSelected = ds === selectedDate;
                const isPast     = ds < todayStr;

                return (
                  <div
                    key={ds}
                    onClick={() => setSelectedDate(ds)}
                    style={{
                      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                      padding: "6px 2px", borderRadius: 8, cursor: "pointer", minHeight: 44,
                      background: isSelected ? COLORS.navy : isToday ? "#EBF3FF" : "transparent",
                      border: `2px solid ${isSelected ? COLORS.navy : isToday ? COLORS.blue : "transparent"}`,
                      transition: "all 0.12s",
                      position: "relative",
                    }}
                    onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = "#F0F4FF"; }}
                    onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = isToday ? "#EBF3FF" : "transparent"; }}
                  >
                    <span style={{
                      fontSize: 14, lineHeight: 1,
                      fontWeight: isToday || isSelected ? 700 : 400,
                      color: isSelected ? "#fff" : isToday ? COLORS.blue : isPast ? "#B0B8C8" : COLORS.text,
                    }}>
                      {day}
                    </span>
                    {has && (
                      <div style={{
                        width: 5, height: 5, borderRadius: "50%", marginTop: 3,
                        background: isSelected ? "rgba(255,255,255,0.8)" : isPast ? "#B0B8C8" : "#00B347",
                      }} />
                    )}
                  </div>
                );
              })}
            </div>

            {/* Legend */}
            <div style={{ borderTop: `1px solid ${COLORS.border}`, padding: "12px 20px", display: "flex", gap: 16, flexWrap: "wrap" }}>
              {[
                { color: COLORS.blue, bg: "#EBF3FF", label: "Today" },
                { color: "#00B347",   bg: "#E9F5E9", label: "Has training" },
                { color: COLORS.navy, bg: COLORS.navy, label: "Selected", textColor: "#fff" },
              ].map(({ color, bg, label, textColor }) => (
                <div key={label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ width: 18, height: 18, borderRadius: 4, background: bg, border: `2px solid ${color}`, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {label === "Has training" && <div style={{ width: 5, height: 5, borderRadius: "50%", background: color }} />}
                  </div>
                  <Text style={{ fontSize: 11, color: COLORS.muted }}>{label}</Text>
                </div>
              ))}
            </div>

            {/* Upcoming list */}
            {upcomingEntries.length > 0 && (
              <div style={{ borderTop: `1px solid ${COLORS.border}`, padding: "14px 20px", flex: 1, overflowY: "auto" }}>
                <Text style={{ fontSize: 11, fontWeight: 700, color: COLORS.muted, textTransform: "uppercase", letterSpacing: 0.6, display: "block", marginBottom: 10 }}>
                  {upcomingLabel}
                </Text>
                {upcomingEntries.map(e => (
                  <div
                    key={e.id}
                    onClick={() => { setSelectedDate(e.date); setYear(new Date(e.date + "T12:00:00").getFullYear()); setMonth(new Date(e.date + "T12:00:00").getMonth()); }}
                    style={{
                      display: "flex", gap: 10, alignItems: "center", padding: "8px 10px",
                      borderRadius: 8, cursor: "pointer", marginBottom: 4,
                      background: e.date === selectedDate ? "#EBF3FF" : "transparent",
                      transition: "background 0.1s",
                    }}
                    onMouseEnter={ev => { if (e.date !== selectedDate) ev.currentTarget.style.background = "#F5F7FF"; }}
                    onMouseLeave={ev => { ev.currentTarget.style.background = e.date === selectedDate ? "#EBF3FF" : "transparent"; }}
                  >
                    <div style={{ width: 36, height: 36, borderRadius: 8, background: COLORS.navy, color: "#fff", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <span style={{ fontSize: 14, fontWeight: 700, lineHeight: 1 }}>{new Date(e.date + "T12:00:00").getDate()}</span>
                      <span style={{ fontSize: 8, opacity: 0.75, textTransform: "uppercase" }}>{MONTHS[new Date(e.date + "T12:00:00").getMonth()].slice(0, 3)}</span>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text style={{ fontSize: 13, fontWeight: 600, color: COLORS.text, display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{e.handlerName}</Text>
                      <Text style={{ fontSize: 11, color: COLORS.muted, display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{e.topic}</Text>
                      {e.time && <Text style={{ fontSize: 11, color: COLORS.blue, display: "block" }}>{formatTime(e.time)}</Text>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Right: Detail panel ── */}
          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
            <DetailPanel />
          </div>
        </div>
      )}

      {/* ── Add/Edit Modal ─────────────────────────────────────────────────────── */}
      <Dialog open={showModal} onOpenChange={(_, d) => { if (!d.open) setShowModal(false); }}>
        <DialogSurface style={{ maxWidth: 520, width: "95vw" }}>
          <DialogTitle style={{ color: COLORS.navy }}>
            {editTarget ? "Edit Training Entry" : "Schedule Training Session"}
          </DialogTitle>
          <DialogBody>
            <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>

              {/* Date display */}
              <div style={{ padding: "10px 14px", background: "#EBF3FF", borderRadius: 8, marginBottom: 18, display: "flex", alignItems: "center", gap: 8 }}>
                <CalendarLtr24Regular style={{ color: COLORS.blue, fontSize: 18, flexShrink: 0 }} />
                <Text weight="semibold" style={{ color: COLORS.navy }}>{formatDisplay(form.date)}</Text>
              </div>

              {/* Handler search */}
              <Field label="Training Handler *" style={{ marginBottom: 14 }}>
                <div style={{ position: "relative" }}>
                  <Input
                    value={form.handlerName}
                    onChange={e => onHandlerSearch(e.target.value)}
                    placeholder="Search by name or type…"
                    contentBefore={userSearching ? <Spinner size="tiny" /> : <Search24Regular />}
                    style={{ width: "100%" }}
                  />
                  {userSuggestions.length > 0 && (
                    <div style={{
                      position: "absolute", top: "100%", left: 0, right: 0, zIndex: 100,
                      background: "#fff", border: `1px solid ${COLORS.border}`, borderRadius: 8,
                      boxShadow: "0 8px 24px rgba(0,0,0,0.12)", overflow: "hidden", marginTop: 2,
                    }}>
                      {userSuggestions.map(u => (
                        <div
                          key={u.mail}
                          onClick={() => pickUser(u)}
                          style={{ padding: "10px 14px", cursor: "pointer", borderBottom: `1px solid ${COLORS.border}`, display: "flex", gap: 10, alignItems: "center" }}
                          onMouseEnter={e => e.currentTarget.style.background = "#F5F7FF"}
                          onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                        >
                          <Avatar name={u.displayName} size={32} color={COLORS.navy} />
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>{u.displayName}</div>
                            <div style={{ fontSize: 11, color: COLORS.muted }}>{u.mail}{u.department ? ` · ${u.department}` : ""}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Field>

              <Field label="Handler Email *" style={{ marginBottom: 14 }}>
                <Input
                  type="email"
                  value={form.handlerEmail}
                  onChange={e => setForm(f => ({ ...f, handlerEmail: e.target.value }))}
                  placeholder="handler@lekoil.com"
                  style={{ width: "100%" }}
                />
              </Field>

              <Field label="Department" style={{ marginBottom: 14 }}>
                <Input
                  value={form.handlerDept}
                  onChange={e => setForm(f => ({ ...f, handlerDept: e.target.value }))}
                  placeholder="e.g. Procurement"
                  style={{ width: "100%" }}
                />
              </Field>

              <Field label="Training Topic *" style={{ marginBottom: 14 }}>
                <Input
                  value={form.topic}
                  onChange={e => setForm(f => ({ ...f, topic: e.target.value }))}
                  placeholder="e.g. Fire Safety Awareness"
                  style={{ width: "100%" }}
                />
              </Field>

              <Field label="Time" style={{ marginBottom: 14 }}>
                <Input
                  type="time"
                  value={form.time}
                  onChange={e => setForm(f => ({ ...f, time: e.target.value }))}
                  style={{ width: "100%" }}
                />
              </Field>

              <Field label="Venue" style={{ marginBottom: 14 }}>
                <Input
                  value={form.venue}
                  onChange={e => setForm(f => ({ ...f, venue: e.target.value }))}
                  placeholder="e.g. Conference Room A (optional)"
                  style={{ width: "100%" }}
                />
              </Field>

              <Field label="Notes">
                <Textarea
                  value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  rows={3}
                  placeholder="Any additional details or instructions… (optional)"
                  style={{ width: "100%", resize: "vertical" }}
                />
              </Field>
            </div>
          </DialogBody>
          <DialogActions>
            <Button appearance="outline" onClick={() => setShowModal(false)} disabled={saving}>Cancel</Button>
            <Button
              appearance="primary"
              onClick={handleSave}
              disabled={saving}
              icon={saving ? <Spinner size="tiny" /> : null}
              style={{ background: COLORS.navy }}
            >
              {saving ? "Saving…" : editTarget ? "Save Changes" : "Schedule"}
            </Button>
          </DialogActions>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
